import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import MainForm from '@/pages/articles-pages/programmes/MultiStepForm/MainForm/MainForm';
import Review from '@/pages/articles-pages/programmes/MultiStepForm/Review/Review';
import MicroSiteTemplatesList from '@/pages/articles-pages/programmes/MultiStepForm/MicroSiteTemplatesList/MicroSiteTemplateList';
import PreviewAndEditMicrosite from '@/pages/articles-pages/programmes/MultiStepForm/PreviewAndEditMicroSite/PreviewAndEditMicrosite';
import { Button, Loader } from '@/components/ui';
import { Paragraph } from '@/components/ui/Typography';
import { cn } from '@/utils/helpers/classnames';
import { useProgramme } from '@/lib/api/hooks/programme';
import { ProgrammeFormData } from '@/lib/api';

export interface IProgrammeMultiStepFormValues extends ProgrammeFormData {
  localCoverImageFile?: File | null;
};



export interface IMultiStepProps {
  prevStep?: () => void;
  nextStep?: () => void;
  handleFormData?: (data: IProgrammeMultiStepFormValues) => void;
  formValues: IProgrammeMultiStepFormValues;
  goToPage?: (page: number) => void;
}

const MultiStepForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id && location.pathname.includes('/edit'));
  const programmeId = isEditMode ? id : '';
  const { data: programmeData, isFetching, isError, refetch } = useProgramme(programmeId || '');


  const DEFAULT_FORM_VALUES: IProgrammeMultiStepFormValues = {
  title: programmeData?.data?.title || '',
  content: programmeData?.data?.content || '',
  coverImageUrl: programmeData?.data?.coverImageUrl || '',
  coverImageTitle: programmeData?.data?.coverImageTitle || '',
  articleTag: programmeData?.data?.articleTag || '',
  landingPageAfsaProgramme: programmeData?.data?.landingPageAfsaProgramme || false,
  allowComments: programmeData?.data?.allowComments || true,
  status: programmeData?.data?.status || 'DRAFT',
  isAddWebsite: programmeData?.data?.isAddWebsite || false,
  websiteType: programmeData?.data?.websiteType || '',
  websiteLink: programmeData?.data?.websiteLink || '',
  microSiteRequest: programmeData?.data?.microSite || null,
  localCoverImageFile: null,
};

  const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState<IProgrammeMultiStepFormValues>(DEFAULT_FORM_VALUES);



  const isMicrositeEnabled = useMemo(
    () => formValues.isAddWebsite && formValues.websiteType === 'MICROSITE',
    [formValues.isAddWebsite, formValues.websiteType],
  );

  useEffect(() => {
    if (!isEditMode || !programmeData?.data) {
      if (!isEditMode) {
        setFormValues(DEFAULT_FORM_VALUES);
      }
      return;
    }

    const programme = programmeData.data;
    const mappedValues = {
      ...DEFAULT_FORM_VALUES,
      articleTag: Array.isArray(programme.articleTag)
        ? programme.articleTag.join(', ')
        : programme.articleTag,
    };

    setFormValues(mappedValues);
  }, [isEditMode, programmeData]);


    const goToPage = (page: number) => {
    setStep(page);
  };

  const nextStep = () => {
    goToPage(step + 1);
  };

  const prevStep = () => {
    goToPage(step - 1);
  };

    const handleInputData = (data: IProgrammeMultiStepFormValues) => {
      setFormValues((prev) => ({
        ...prev,
        ...data,
      }));
  };


  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <MainForm
            nextStep={nextStep}
            prevStep={() => navigate(programmeId ? `/admin/programmes/${programmeId}` : '/admin/programmes')}
            goToPage={goToPage}
            handleFormData={handleInputData} formValues={formValues}
          />
        );

      case 2:
        return (
          <MicroSiteTemplatesList
            nextStep={nextStep}
            prevStep={prevStep}
            handleFormData={handleInputData}
            formValues={formValues}
          />
        );

      case 3:
        return (
          <PreviewAndEditMicrosite
            nextStep={nextStep}
            prevStep={prevStep}
            handleFormData={handleInputData}
            formValues={formValues}
          />
        );

      case 4:
        return (
          <Review
            formValues={formValues}
            prevStep={prevStep}
            goToPage={goToPage}
          />
        );

      default:
        return null;
    }
  };

  if (isFetching) {
    return <Loader />;
  }

  if (isError) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-2 text-center'>
        <Paragraph className='text-center text-error'>Failed to load programme details. Please try again.</Paragraph>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={cn('relative flex w-full flex-1 flex-col gap-4')}>
      <div className="rounded-xl border border-gray-200 bg-white p-4">

        {/* Step Indicators */}
        <div className="flex items-center gap-2 text-sm">
          <span className={cn('rounded px-3 py-1', step === 1 ? 'bg-[#047857] text-white' : 'bg-gray-100 text-gray-600')}>
            Main Form
          </span>

          {isMicrositeEnabled ? (
            <>
              <span className={cn('rounded px-3 py-1', step === 2 ? 'bg-[#047857] text-white' : 'bg-gray-100 text-gray-600')}>
                Microsite Templates
              </span>
              <span className={cn('rounded px-3 py-1', step === 3 ? 'bg-[#047857] text-white' : 'bg-gray-100 text-gray-600')}>
                Microsite Preview
              </span>
            </>
          ) : null}

          <span className={cn('rounded px-3 py-1', step === 4 ? 'bg-[#047857] text-white' : 'bg-gray-100 text-gray-600')}>
            Review
          </span>
        </div>
      </div>

      {renderStep()}
    </div>
  );
};

export default MultiStepForm;
